import { useState } from "react";

// Deterministic, fully offline placeholder — never fetched, never generated.
// Decorative only: the overview card always shows the assessment title as
// visible text right next to this icon, so it's aria-hidden rather than
// given a redundant alt-equivalent label.
function GenericAssessmentIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="11" fill="currentColor" fillOpacity="0.22" />
      <path
        d="M12 5.5c.3 0 .58.2.67.48l1.1 3.4 3.4 1.1a.7.7 0 0 1 0 1.34l-3.4 1.1-1.1 3.4a.7.7 0 0 1-1.34 0l-1.1-3.4-3.4-1.1a.7.7 0 0 1 0-1.34l3.4-1.1 1.1-3.4c.09-.28.37-.48.67-.48Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Wraps a CDN-hosted dashboard image with a same-sized inline SVG fallback,
// so a missing mapping or a failed/not-yet-uploaded CDN file never leaves a
// broken <img> icon on the card. `src` is expected to already be the fully
// built, signed CDN URL (or "" / null when the assessment has no known
// mapping) — this component only decides what to render, never how the URL
// is built.
//
// This instance is reused across different `src` values without
// remounting (e.g. the dashboard overview card keeps the same
// DashboardAssessmentImage instance as the user selects different
// completed assessments) — so failure state is keyed to the specific src
// that failed, not a plain boolean. A plain boolean would "stick" once any
// one src ever failed, permanently hiding every later, perfectly valid src
// behind the fallback until the component happened to remount.
export default function DashboardAssessmentImage({ src, alt = "", className }) {
  const [erroredSrc, setErroredSrc] = useState(null);

  if (!src || src === erroredSrc) {
    return <GenericAssessmentIcon className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErroredSrc(src)}
    />
  );
}
